// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useForm } from 'react-final-form';
import { InputGroup } from 'react-bootstrap';
import classNames from 'classnames';
import _ from 'lodash';
import { Button, Icon } from '@seeqdev/qomponents';
import { ValidatingFormComponent } from '@/formbuilder/formBuilder.constants';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { SelectAssetModal } from '@/core/SelectAssetModal.molecule';
import { itemIconClass } from '@/utilities/utilities';
import { getAssetFromAncestors } from '@/utilities/httpHelpers.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqItemsApi } from '@/sdk';

export interface SelectSearchItemIF extends ValidatingFormComponent<any> {
  component: 'SelectSearchItemFormComponent';
  iconPartialTooltipKey: string;
  scopeIds?: string[];
  excludeGloballyScoped?: boolean;
  helpTextKey?: string;
  customErrorText?: string;
  title: string;
  notSelectedMessage: string;
  searchTypes?: string[];
  isMultiple?: boolean;
  onRemove?: (item: any) => void;
  showAddIcon?: boolean;
  onDelete?: () => void;
}

export const SelectSearchItemFormComponent: React.FunctionComponent<SelectSearchItemIF> = (props) => {
  const {
    name,
    value,
    testId = 'selectAsset',
    validation,
    extendValidation,
    onChange,
    iconPartialTooltipKey,
    helpTextKey = undefined,
    customErrorText,
    scopeIds = undefined,
    excludeGloballyScoped = false,
    title,
    notSelectedMessage,
    searchTypes,
    isMultiple = false,
    onRemove,
    showAddIcon = false,
    required = true,
    onDelete,
  } = props;

  const { t } = useTranslation();

  const formState = useForm().getState();

  const showError =
    _.has(formState.errors, name) &&
    (_.has(formState.dirtyFields, name) || _.has(formState.dirtyFieldsSinceLastSubmit, name)) &&
    formState.hasValidationErrors;
  const [isSelectingItem, setIsSelectingItem] = useState(false);
  const [selectedItem, setSelectedItem] = useState(value);
  const [formattedItem, setFormattedItem] = useState([]);
  useEffect(() => setSelectedItem(value), [value]);

  useEffect(() => {
    if (value) {
      const names: string[] = [];
      const datasources: string[] = [];
      const items = Array.isArray(value) ? value : [value];
      items.map(async (item) => {
        if (!item?.id) {
          const result = await sqItemsApi.getItemAndAllProperties({ id: item });
          names.push(result.data.name);
          datasources.push(result.data?.datasource?.name);
          setFormattedItem([
            {
              name: names,
              datasource: datasources,
            },
          ]);
        }
      });
    }
  }, [value]);

  const defaultValidation = (value: any) => required && !value;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const validateItem = (item) => Promise.resolve(!appliedValidation(item));
  const [canSave, setCanSave] = useState(false);
  useEffect(() => {
    isSelectingItem &&
      validateItem(selectedItem).then((result) => {
        setCanSave(result);
      });
  }, [selectedItem, isSelectingItem]);

  const clearAndClose = () => {
    setIsSelectingItem(false);
  };

  const onSave = () => {
    onChange(selectedItem);
    setIsSelectingItem(false);
  };

  const itemIcon = (item: any) => {
    return (
      <Icon
        icon={itemIconClass(item)}
        testId={`${testId}-icon`}
        type="inherit"
        extraClassNames="sq-fairly-dark-gray pl5 pr10"
        large={true}
      />
    );
  };

  const noSelectedItemResult = () => {
    return (
      <div className="sq-fairly-dark-gray text-italic" data-testid={`${testId}-notSelected`}>
        <span>{t(notSelectedMessage)}</span>
      </div>
    );
  };

  const formatItemName = (item: any, index = undefined) => {
    if (item?.id) {
      return item.name;
    }
    return formattedItem[0]?.name?.[index ?? 0];
  };

  const formatItemAncestors = (item: any, index = undefined) => {
    if (item?.ancestors) {
      return getAssetFromAncestors(item.ancestors).formattedName;
    }
    return item?.id ? item?.datasource?.name : formattedItem[0]?.datasource?.[index ?? 0];
  };

  const searchResult = (item: any) => (
    <div className="flexColumnContainer flexSpaceBetween flexFill ptb2">
      {(item && (
        <>
          <div className="flexColumnContainer flexCenter">{itemIcon(item)}</div>
          <div className="flexFill" data-testid={`${testId}-selected`}>
            <div className="searchResultName">
              <span className="simple-word-break">{formatItemName(item)}</span>
            </div>
            <div className="xsmall pb2 sq-fairly-dark-gray text-italic simple-word-break">
              {formatItemAncestors(item)}
            </div>
          </div>
        </>
      )) ||
        noSelectedItemResult()}
    </div>
  );

  const searchResultMultiple = (items: any) => (
    <div className="flexRowContainer flexSpaceBetween flexFill ptb2">
      {items?.length > 0
        ? _.map(items, (item, index) => {
            return (
              <div className="flexRowContainer flexFill removableSearchResult">
                <div className="flexColumnContainer flexCenter flexFill">
                  {itemIcon(item)}
                  <div className="searchResultName flexFill">
                    <span className="simple-word-break">{formatItemName(item, index)}</span>
                    <span className="xsmall ml5 pb2 sq-fairly-dark-gray text-italic simple-word-break">
                      {formatItemAncestors(item, index)}
                    </span>
                  </div>
                  <Icon
                    icon="fa-close"
                    type="text"
                    onClick={() => onRemove(item)}
                    testId="removeSearchResult"
                    tooltip={t('Remove item from list')}
                    extraClassNames="mr10 cursorPointer"
                  />
                </div>
              </div>
            );
          })
        : noSelectedItemResult()}
    </div>
  );

  const body = helpTextKey ? (
    <div className="alert alert-info" data-testid={`${testId}-helpText`}>
      {t(helpTextKey)}
    </div>
  ) : (
    <></>
  );

  const footer = (
    <div>
      <Button onClick={clearAndClose} label={t('CANCEL')} extraClassNames="mr5" testId={`${testId}-cancel`} />
      <Button
        extraClassNames={canSave ? '' : 'noMouse'}
        onClick={onSave}
        label={t('SAVE')}
        variant="theme"
        disabled={!canSave}
        testId={`${testId}-save`}
      />
    </div>
  );

  return (
    <>
      <FormFieldWrapper
        wrapperClassNames="flexFill"
        testId={testId}
        showError={showError}
        customErrorText={customErrorText}>
        <Field name={name} validate={appliedValidation}>
          {({ input, meta }) => {
            const formFieldProps = getFormFieldProps(formState, input, meta, props);
            return (
              <InputGroup>
                <div
                  className={classNames(
                    'width-maximum form-control ptb0 flexColumnContainer flexAlignCenter cursorPointer multiSelectSearchResult',
                    formFieldProps.extraClassNames,
                  )}
                  onClick={() => {
                    (!isMultiple || (isMultiple && !value?.length)) && setIsSelectingItem(true);
                  }}
                  data-testid={`${testId}-value`}>
                  {isMultiple ? searchResultMultiple(formFieldProps.value) : searchResult(formFieldProps.value)}
                </div>
                <InputGroup.Append>
                  {onDelete && selectedItem && (
                    <Button icon="fa-trash" iconStyle="theme" testId={`${testId}-delete`} onClick={() => onDelete()} />
                  )}
                  <Button
                    icon={showAddIcon ? 'fa-plus' : 'fa-pencil'}
                    iconStyle="theme"
                    testId={`${testId}-edit`}
                    onClick={() => {
                      setIsSelectingItem(true);
                    }}
                  />
                </InputGroup.Append>
              </InputGroup>
            );
          }}
        </Field>
      </FormFieldWrapper>
      {isSelectingItem && (
        <SelectAssetModal
          header={{
            title: t(title),
          }}
          body={body}
          footer={footer}
          onClose={clearAndClose}
          assetId={selectedItem?.id}
          iconPartialTooltipKey={iconPartialTooltipKey}
          onSelect={setSelectedItem}
          validateItem={validateItem}
          scopeIds={scopeIds}
          excludeGloballyScoped={excludeGloballyScoped}
          testId={`${testId}-modal`}
          searchTypes={searchTypes ?? [SeeqNames.Types.Asset, SeeqNames.Types.AssetSelection]}
        />
      )}
    </>
  );
};
